import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from '@mui/material/Box';

import { assign, cloneDeep, isEmpty, isEqual, isNull, isUndefined } from "lodash";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { format, isValid } from "date-fns";

import monthlyCategoryLocationService from "@plannel/services/ip/monthly-category-location-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import SplitterContainer from "@plannel/components/SplitterContainer";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { IPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";

import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import axiosUtil from "@plannel/utils/axios-bigint";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import { IPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import ItemTypeFilter, { ITEM_TYPE } from "@plannel/components/filter/ItemTypeFilter";

const CURRENT_DATE = new ZDate();
const INITIAL_START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth() + 1, 1);
const INITIAL_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const MAX_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const INITIAL_PERIOD = { start: INITIAL_START_DATE, end: INITIAL_END_DATE };

const TOTAL = "total";
const AVG = "avg";
const STDDEV = "stddev";
const CV = "cv";
const EXCEL_EXPORT = "excelExport";
const AGGREGATE_COLUMNS = [TOTAL, AVG, STDDEV, CV];
const MEASURE = CodeUtil.ipMeasure;
const { snackBarMessageColor } = ColorUtil;
const MonthlyByCategoryAndLocation = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const userInfo = axiosUtil.jsonbigParse(localStorage.getItem("user"));
  const curreny = userInfo?.currencySymbol;

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [periodFilter, setPeriodFilter] = useState(
    reduxViewState?.period ?
      { start: new ZDate(reduxViewState.period.start), end: new ZDate(reduxViewState.period.end) }
      : INITIAL_PERIOD
  );
  const [itemTypeFilter, setItemTypeFilter] = useState(reduxViewState?.itemType ? reduxViewState.itemType : null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [columnDefs, setColumnDefs] = useState([]);
  const [csvColDef, setCsvColDef] = useState([]);
  const [gridData, setGridData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [displayName, setDisplayName] = useState(reduxViewState?.displayName ?? null);
  const [gridLoading, setGridLoading] = useState(false);
  const getDisplayText = (translationKey, originTxt) => {
    if (displayName != null && displayName[translationKey]) {
      return displayName[translationKey];
    }
    return t(originTxt);
  };

  const defaultSeries = [
    { field: MEASURE.SKU, text: getDisplayText(MEASURE.SKU, MEASURE.SKU), type: "bar", orientation: "left", groupName: t("sku") },
    { field: MEASURE.ACTIVE_SKU, text: getDisplayText(MEASURE.ACTIVE_SKU, MEASURE.ACTIVE_SKU), type: "bar", orientation: "left", groupName: t("sku") },
    { field: MEASURE.AVG_NO_OF_LOCATION, text: getDisplayText(MEASURE.AVG_NO_OF_LOCATION, MEASURE.AVG_NO_OF_LOCATION), type: "line", orientation: "right" },
    { field: MEASURE.SALES_QTY, text: getDisplayText(MEASURE.SALES_QTY, MEASURE.SALES_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.PROFIT, text: getDisplayText(MEASURE.PROFIT, MEASURE.PROFIT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.SALES_AMT, text: getDisplayText(MEASURE.SALES_AMT, MEASURE.SALES_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.STOCK_QTY, text: getDisplayText(MEASURE.STOCK_QTY, MEASURE.STOCK_QTY), type: "bar", orientation: "left", groupName: t("qty") },
    { field: MEASURE.STOCK_AMT, text: getDisplayText(MEASURE.STOCK_AMT, MEASURE.STOCK_AMT), type: "line", orientation: "right", groupName: t("amt"), isAmt: true },
    { field: MEASURE.INV_DAYS, text: getDisplayText(MEASURE.INV_DAYS, MEASURE.INV_DAYS), type: "line", orientation: "right" },
    { field: MEASURE.INV_TURNOVER, text: getDisplayText(MEASURE.INV_TURNOVER, MEASURE.INV_TURNOVER), type: "line", orientation: "right" }
  ]
  const [pivotConfig, setPivotConfig] = useState({
    groupKey: "measure"
  });

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);

  /*
   * defaultSeries 는 렌더마다 t() 로 다시 만들어지는데, 여기서 걸러낸 결과를 state 에 담아 두면
   * 언어를 바꿔도 차트가 옛 언어로 남는다. 선택된 measure 목록에서 렌더 시점에 파생시킨다.
   */
  const chartSeries = useMemo(
    () => defaultSeries.filter(series => measures.includes(series.field)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [measures, displayName, t]
  );

  /* 차트에 넘기는 설정 — 시리즈는 state 에 굳히지 않고 렌더 시점에 합친다. */
  const chartPivotConfig = useMemo(() => ({ ...pivotConfig, chartSeries }), [pivotConfig, chartSeries]);
  const allMeasure = useMemo(
    () => [MEASURE.SKU, MEASURE.ACTIVE_SKU, MEASURE.AVG_NO_OF_LOCATION, MEASURE.SALES_QTY, MEASURE.PROFIT, MEASURE.SALES_AMT, MEASURE.STOCK_QTY, MEASURE.STOCK_AMT, MEASURE.INV_DAYS, MEASURE.INV_TURNOVER, MEASURE.YOY_PER, MEASURE.MOM_PER],
    []
  );

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const gridRef = useRef();
  const chartRawRowsRef = useRef([]); // 번역 전 차트 행 — 언어 변경 시 재조립용

  const valueFormatter = ({ value }) => {
    return GridUtils.gridValueL10N(value);
  };

  const defaultColumnDefs = [
    {
      headerName: "measure", field: "measure", type: ["nonEditableColumn"], width: 150, pinned: "left", lockPinned: true,
      valueFormatter,
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isClicked: setMeasureModalOpen
      }
    }
  ];

  const getRequestParams = () => {
    const params = {};

    if (filterValue?.category) {
      params.categoryCd = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (filterValue?.locations) {
      params.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (itemTypeFilter) {
      params.itemType = itemTypeFilter;
    }

    if (periodFilter?.start) {
      params.startDate = periodFilter.start;
    }

    if (periodFilter?.end) {
      params.endDate = periodFilter.end;
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    return params;
  };

  const formatterByMeasure = (params, isExport = false) => {
    const { data, colDef } = params;
    const fieldName = colDef?.field
    const value = data?.[fieldName];

    if (isNull(value) || isUndefined(value) || isNaN(value)) {
      return "";
    }

    const amtColumns = [MEASURE.SALES_AMT, MEASURE.PROFIT, MEASURE.STOCK_AMT];
    const rationalColumns = [MEASURE.AVG_NO_OF_LOCATION, MEASURE.INV_DAYS, MEASURE.INV_TURNOVER];
    const percentColumns = [MEASURE.YOY_PER, MEASURE.MOM_PER];
    const turnoverColumn = MEASURE.INV_TURNOVER;

    let digits = rationalColumns.includes(data.originMeasure) ? 2 : 0;
    if (isEqual(CV, fieldName) || isEqual(turnoverColumn, data.originMeasure)) {
      digits = 2;
    }

    if (percentColumns.includes(data.originMeasure)) {
      if (isExport) {
        return NumberUtil.formatNumber(value, { maximumFractionDigits: 4, minimumFractionDigits: 4 }).replaceAll(",", "");
      } else {
        return NumberUtil.formatNumber(value, { style: "percent", maximumFractionDigits: 2, minimumFractionDigits: 0 })
      }
    }

    let formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: digits, minimumFractionDigits: digits });
    if (amtColumns.includes(data.originMeasure)) {
      formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    }
    if(isExport) {
      return formatValue.replace(/,/g, "");
    }

    if (amtColumns.includes(data.originMeasure) && ![CV, STDDEV].includes(fieldName)) {
      return value >= 0 ? curreny + formatValue : `-${curreny}${formatValue.replace("-", "")}`;
    } 

    return formatValue;
  }
  formatterByMeasure.customType = EXCEL_EXPORT;

  const headerFormatter = (value) => {
    if (isValid(new ZDate(value))) {
      return format(new ZDate(value), "yyyy-MM");
    } else {
      return value;
    }
  }

  const setGridHeaders = (headers) => {
    if (headers.length <= 0) {
      setColumnDefs(defaultColumnDefs);
      return;
    }

    const colDefs = cloneDeep(defaultColumnDefs);

    headers.forEach((header) => {
      colDefs.push({
        headerName: header, field: header, width: 120, sortable: false,
        colId: GridUtils.IGNORE_P13N_COLLID + '_' + header,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        valueFormatter: formatterByMeasure
      });
    });
    setColumnDefs(colDefs);
  }

  const getAggData = (pivotData) => {
    const count = pivotData.length;

    if (count === 0) {
      return { total: null, avg: null, stddev: null, cv: null };
    }

    // Total & Avg
    const values = pivotData;
    const total = values.reduce((prev, current) => prev + current, 0);
    const avg = total / count;

    // stdDev
    let powSum = 0;
    for (const value of values) {
      powSum += Math.pow(value - avg, 2);
    }

    const stddev = Math.sqrt(powSum / count);
    const cv = avg !== 0 ? stddev / avg : 0;
    return { total, avg, stddev, cv };
  }

  const setChart = (datas) => {
    const chartDatas = [];

    chartRawRowsRef.current = datas ?? [];

    if (datas && datas.length <= 0) {
      setChartData([]);
      return;
    }

    for (const row of datas) {
      const data = {...row};
      data.measure = t(data.measure);
      chartDatas.push(data)
    }
    setChartData(chartDatas);
  }

  /*
   * 언어 변경 — 차트 데이터의 measure 값은 t() 로 번역된 문자열이고, PivotChartComposed 는 그 값을
   * 시리즈의 text 와 맞춰(chartSeries.find(text === dataKey)) 무엇을 그릴지 고른다. 시리즈만 새
   * 언어가 되면 짝이 깨져 그래프가 통째로 사라지므로, 데이터도 같이 다시 만든다(재조회 없음).
   */
  useEffect(() => {
    if (isEmpty(chartRawRowsRef.current)) {
      return;
    }
    setChart(chartRawRowsRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const setGridDatas = (headers, results) => {
    const datas = [];

    const skusPivotData = "skusPivotData";
    const activeSkusPivotData = "activeSkusPivotData";
    const avgNoOfLocationPivotData = "avgNoOfLocationPivotData";
    const salesQtyPivotData = "salesQtyPivotData";
    const salesAmtPivotData = "salesAmtPivotData";
    const stockQtyPivotData = "stockQtyPivotData";
    const stockAmtPivotData = "stockAmtPivotData";
    const invDaysPivotData = "invDaysPivotData";
    const invTurnoverPivotData = "invTurnoverPivotData";
    const profitPivotData = "profitPivotData";
    const invYoyAmtPerPivotData = "invYoyAmtPerPivotData";
    const invMomAmtPerPivotData = "invMomAmtPerPivotData";

    for (const resultsData of results) {
      const measure = isEmpty(measures) ? allMeasure : measures;
      
      for (const measureName of measure) {
        const display = displayName?.[measureName];
        const data = { measure: display != null && !isEmpty(display) ? display : measureName, originMeasure: measureName };

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];
          let aggData = {};

          switch (measureName) {
            case MEASURE.SKU:
              data[header] = resultsData[skusPivotData][idx];
              aggData = getAggData(resultsData[skusPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.ACTIVE_SKU:
              data[header] = resultsData[activeSkusPivotData][idx];
              aggData = getAggData(resultsData[activeSkusPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.AVG_NO_OF_LOCATION:
              data[header] = resultsData[avgNoOfLocationPivotData][idx];
              aggData = getAggData(resultsData[avgNoOfLocationPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.SALES_QTY:
              data[header] = resultsData[salesQtyPivotData][idx];
              aggData = getAggData(resultsData[salesQtyPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.SALES_AMT:
              data[header] = resultsData[salesAmtPivotData][idx];
              aggData = getAggData(resultsData[salesAmtPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.STOCK_QTY:
              data[header] = resultsData[stockQtyPivotData][idx];
              aggData = getAggData(resultsData[stockQtyPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.STOCK_AMT:
              data[header] = resultsData[stockAmtPivotData][idx];
              aggData = getAggData(resultsData[stockAmtPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.INV_DAYS:
              data[header] = resultsData[invDaysPivotData][idx];
              aggData = getAggData(resultsData[invDaysPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.INV_TURNOVER:
              data[header] = resultsData[invTurnoverPivotData][idx];
              aggData = getAggData(resultsData[invTurnoverPivotData]);
              assign(data, aggData);
              break;
          
            case MEASURE.PROFIT:
              data[header] = resultsData[profitPivotData][idx];
              aggData = getAggData(resultsData[profitPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.YOY_PER:
              data[header] = resultsData[invYoyAmtPerPivotData][idx];
              aggData = getAggData(resultsData[invYoyAmtPerPivotData]);
              assign(data, aggData);
              break;

            case MEASURE.MOM_PER:
              data[header] = resultsData[invMomAmtPerPivotData][idx];
              aggData = getAggData(resultsData[invMomAmtPerPivotData]);
              assign(data, aggData);
              break;

            default:
            break;
          }
        }
        delete data.hrchyCd;
        datas.push(data);
      }
    }
    setChart(datas);
    return datas;
  }

  const retrieveMonthlyTrendCategoryLocation = () => {
    gridRef.current?.api?.setGridOption('loading', true);

    const params = getRequestParams();
    monthlyCategoryLocationService.getAll(params)
      .then(response => {
        const { pivotResult } = response.data;
        if (pivotResult) {
          const { localDateHeader, data } = pivotResult;
          if (pivotResult?.data && pivotResult.data.length > 0) {
            const headers = localDateHeader.map(header => headerFormatter(header));
            // chartSeries 는 chartPivotConfig 에서 렌더 시점에 합친다(번역문을 state 에 굳히지 않는다).
            setPivotConfig({
              ...pivotConfig,
              include: headers
            });
            const newHeader = [...headers, ...AGGREGATE_COLUMNS];
            setGridHeaders(newHeader);
            setGridData(setGridDatas(newHeader, data));
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
            setGridHeaders([]);
            setGridData([]);
            setChartData([]);
            setPivotConfig({
              ...pivotConfig,
              include: []
            });
          }
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        const pageState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, displayName });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        const reduxState = { ...pageState, ...filterState };
        reduxDispatch(reduxUtil.updateViewState(reduxState));

        gridRef.current?.api?.setGridOption('loading', false);
      });
  };

  const updateSeriesState = series => {
    const fieldsOnly = series?.map(s => ({ field: s.field }));
    const reduxState = reduxUtil.createObjectState(viewName, { series: fieldsOnly, displayName });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
  };

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);
  }

  const retrieveP13n = () => {
    planConfigService.getMeasureDisplayNames(CodeUtil.module.IP)
      .then(response => {
        const { data } = response;
        if (data && !isEmpty(data?.objVal)) {
          setDisplayName(data.objVal);
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setGridLoading(true)
      });
  }

  useEffect(() => {
    if(!isEmpty(columnDefs)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true });
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs])

  useEffect(() => {
    if (!isEmpty(measures)) {
      updateSeriesState(chartSeries);
      retrieveMonthlyTrendCategoryLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measures, gridLoading]);

  useEffect(() => {
    if (!isEmpty(measures)) {
      retrieveMonthlyTrendCategoryLocation();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, periodFilter, itemTypeFilter]);

  useEffect(() => {
    if (columnDefs.isEmpty) {
      return;
    }
    const columns = cloneDeep(columnDefs)
    setCsvColDef(columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs])

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    retrieveP13n()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs/>
        <IPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <PeriodFilter viewName={viewName} period={periodFilter} setPeriod={setPeriodFilter} dateType={CodeUtil.buckets.MONTH} maxDate={MAX_END_DATE}/>
        <ItemTypeFilter viewName={viewName} itemTypeValue={itemTypeFilter} onChangeFilter={setItemTypeFilter} itemTypeFilters={[ITEM_TYPE.KIT, ITEM_TYPE.FG]}/>
        <IPItemAutocomplete retrieve={retrieveMonthlyTrendCategoryLocation} searchValue={itemSearch} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ml: "auto"}}>
          <ExcelExportButton
            title={title}
            service={monthlyCategoryLocationService}
            columnDefs={csvColDef}
            params={{...getRequestParams(), isPivot: true, }}
            setExcelData={setGridDatas}
            headerFormatter={headerFormatter}
          />
        </Box>
      </FilterContainer>      <SplitterContainer initialSizes={[1, 1]} collapseDirection='up'>
        <Box className="chart-area" sx={{ height: "100%" }}>
          <PivotChartComposed
            key={`${measures.join(',')}-${chartSeries.length}`}
            pivotConfig={chartPivotConfig}
            data={chartData}
            allDataSeries={chartSeries}
            options={{ decimalPoint: true, key: "chart" }}
            nullConvert
            activeMeasures={measures}
          />
        </Box>
        <Box className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 20px)", mt: 2 }}>
          <AgGridReact
            ref={gridRef}
            rowData={gridData}
            {...defaultGridMemo}
          >
          </AgGridReact>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
        displayNames={displayName}
        hasDisplayName={true}
      />
    </>
  );
};

export default withTranslation()(MonthlyByCategoryAndLocation);